import React, { useEffect, useState } from "react";
import { Check, Sparkles } from "lucide-react";

import { AdminScreen, Chips, OPTS, Section, Toggle } from "@/admin/kit";
import { api } from "@/lib/api";
import { Button, Spinner } from "@/components/kit";
import { useApp } from "@/store";

const CONCERNS = ["frizz", "dryness", "tangles", "flat", "unruly", "low-maintenance"];
const PATTERNS = ["1A", "1B", "1C", "2A", "2B", "2C", "3A", "3B", "3C", "4A", "4B", "4C"];

export default function AdminRules() {
  const { toast, refreshRecs } = useApp();
  const [r, setR] = useState(null);
  const [p, setP] = useState({ hair_pattern: "2B", hair_type: "wavy", length: "medium", face_shape: "oval", density: "medium", concerns: [] });
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api("/admin/rules").then(setR).catch((e) => toast(e.message, "error"));
  }, [toast]);
  const save = async () => {
    try {
      setR(await api("/admin/rules", { method: "PUT", body: r }));
      refreshRecs();
      toast("Rules saved — live in the app now.", "success");
    } catch (e) {
      toast(e.message, "error");
    }
  };
  const run = async () => {
    setBusy(true);
    try {
      setPrev(await api("/admin/rules/preview", { method: "POST", body: { profile: p } }));
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };
  const setp = (k) => (v) => setP((x) => ({ ...x, [k]: k === "concerns" ? v : v[0], ...(k === "hair_pattern" ? { hair_type: OPTS.hair_types[Number(v[0][0]) - 1] } : {}) }));

  return (
    <AdminScreen title="Recommendation rules" testId="admin-rules">
      <p className="t-caption">Per-hairstyle products, reason text, amounts and time are edited on each hairstyle.</p>
      <div className="grid items-start gap-5 md:grid-cols-2">
        <Section title="Global rules" testId="admin-rules-global">
          {!r ? <Spinner /> : (
            <>
              <Chips testId="admin-rules-concerns" label="Concerns that add BREEZ Leave-In Conditioner" options={CONCERNS} value={r.leave_in_concerns} onChange={(v) => setR({ ...r, leave_in_concerns: v })} />
              <Toggle testId="admin-rules-auto-adjust" label="Auto-adjust product for hair type (e.g. curly → no clay)" value={r.auto_adjust} onChange={(v) => setR({ ...r, auto_adjust: v })} />
              <Button testId="admin-rules-save" title="Save rules" icon={Check} onClick={save} />
            </>
          )}
        </Section>
        <Section title="Preview" note="Pick a sample profile to see the top 5 and products.">
          <Chips testId="admin-preview-pattern" single label="Hair pattern" options={PATTERNS} value={[p.hair_pattern ?? ""]} onChange={setp("hair_pattern")} />
          <Chips testId="admin-preview-length" single label="Length" options={OPTS.lengths} value={[p.length ?? ""]} onChange={setp("length")} />
          <Chips testId="admin-preview-face" single label="Face shape" options={OPTS.faces} value={[p.face_shape ?? ""]} onChange={setp("face_shape")} />
          <Chips testId="admin-preview-hold" single label="Hold" options={OPTS.hold} value={[p.hold ?? ""]} onChange={setp("hold")} />
          <Chips testId="admin-preview-concerns" label="Concerns" options={CONCERNS} value={p.concerns ?? []} onChange={setp("concerns")} />
          <Button testId="admin-preview-run" title="Preview top 5" icon={Sparkles} loading={busy} onClick={run} />
        </Section>
      </div>
      {prev ? (
        <Section title="Preview result" testId="admin-preview-result">
          {prev.map((x, i) => (
            <div key={x.slug} data-testid={`admin-preview-${i + 1}`} className="flex flex-col gap-1 rounded-xl bg-white p-3">
              <p className="t-strong">{i + 1}. {x.name} <span className="t-caption">(score {x.score})</span></p>
              <p className="t-caption">{x.explanation}</p>
              <p className="t-body">Primary: {x.match.primary?.name ?? "none (all suitable products inactive)"}{x.match.supporting ? ` · Supporting: ${x.match.supporting.name}` : ""}</p>
              <p className="t-caption">{x.match.primary?.reason}</p>
            </div>
          ))}
        </Section>
      ) : null}
    </AdminScreen>
  );
}
